import { useState } from 'react';
import { format } from 'date-fns';
import { ChevronDown, ChevronRight, FileSignature } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { usePublicShareGeneratedDocuments } from '../api/public-share-api';
import type { PublicGeneratedDocument } from '../types';
import { PublicSection } from './PublicSection';

function DocumentRow({ d }: { d: PublicGeneratedDocument }) {
  const [open, setOpen] = useState(false);
  const hasContent = !!d.content && d.content.trim().length > 0;

  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{d.template_name ?? 'Document'}</span>
            {d.status && (
              <Badge variant="outline" className="text-xs capitalize">
                {d.status}
              </Badge>
            )}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            Generated {format(new Date(d.generated_at), 'MMM d, yyyy HH:mm')}
          </div>
        </div>
        {hasContent && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Content
          </button>
        )}
      </div>
      {open && hasContent && (
        <div className="mt-2 rounded bg-muted/40 p-3 text-xs leading-relaxed whitespace-pre-wrap">
          {d.content}
        </div>
      )}
    </li>
  );
}

export function PublicDocumentsList({ token }: { token: string }) {
  const { data, isLoading } = usePublicShareGeneratedDocuments(token, true);
  const rows = data?.data ?? [];
  return (
    <PublicSection
      title="Generated documents"
      icon={FileSignature}
      count={rows.length}
      isLoading={isLoading}
      emptyMessage="No generated documents to show."
    >
      <ul className="divide-y">
        {rows.map((d) => (
          <DocumentRow key={d.id} d={d} />
        ))}
      </ul>
    </PublicSection>
  );
}
